import { baseUrl } from "../common/config";
import { http } from "../common/helpers";
import { ApiResponse } from "../models/api";
import { Status } from "../models/gameplan";

export interface QuickWinApi {
  id: number;
  name: string;
  status: Status;
  actionStep: string;
}

export const postFocus = async ({
  participantProfileId,
  focusId,
}: {
  participantProfileId: string;
  focusId: number;
}) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/focus/participant-focus`,
    { participantProfileId, focusId }
  );
  return data.data;
};

export const patchFocus = async ({
  participantProfileId,
  commitedTime,
}: {
  participantProfileId: string;
  commitedTime: string;
}) => {
  const { data } = await http.patch(
    `${baseUrl}/api/assessments/focus/participant-focus`,
    { participantProfileId, commitedTime }
  );
  return data.data;
};

interface SaveWorkoutParams {
  workoutId: number;
  status: Status;
  answer?: string;
}

export const saveWorkout = async (params: SaveWorkoutParams) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/workout/participant-status`,
    params
  );
  return data.data;
};

export const getQuickwins = async (courseId: number) => {
  const { data } = await http.get<ApiResponse<QuickWinApi[]>>(
    `${baseUrl}/api/assessments/course/${courseId}/quickwins`
  );

  return data.data;
};

export const setDiscoverStatus = async (params) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/participant-lesson/discover/${params.lessonId}/${params.slideStatus}`
  );
  return data.data;
};

export const setIntroStatus = async (params) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/participant-lesson/${params.lessonId}/${params.status}`
  );
  return data.data;
};

export const setQuickWinStatus = async ({
  id,
  status,
}: {
  id: number;
  status: string;
}) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/quickwin/participant-status/${id}/${status}`
  );
  return data.data;
};

interface HighlightParams {
  id?: number;
  title?: string;
  note: string;
  type: string;
  slideId: number;
}

export const addHighlight = async (params: HighlightParams) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/highlights-gameplan`,
    params
  );
  return data.data;
};

export const updateHighlight = async (params: HighlightParams) => {
  const { data } = await http.put(
    `${baseUrl}/api/assessments/highlights-gameplan`,
    params
  );
  return data.data;
};

export const getHighlightBySlideId = async (slideId: number) => {
  const { data } = await http.get(
    `${baseUrl}/api/assessments/highlights-gameplan/slide/${slideId}`
  );
  return data.data;
};

export const getParticipantLessonCategories = async () => {
  const { data } = await http.get(
    `${baseUrl}/api/assessments/participant-lesson-category`
  );
  return data.data;
};

interface LessonCollectableCategoryParams {
  id?: number;
  name: string;
  isPrivate: boolean;
}

export const addLessonCollectableCategory = async (
  params: LessonCollectableCategoryParams
) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/participant-lesson-category`,
    params
  );
  return data.data;
};

interface LessonCollectableParams {
  categoryId?: number;
  slideId: number;
}

export const addLessonCollectable = async (params: LessonCollectableParams) => {
  const { data } = await http.post(
    `${baseUrl}/api/assessments/participant-lesson-collectable`,
    params
  );
  return data.data;
};

interface ParticipantLessonCollectableApi {
  totalRecords: number;
  data: ParticipantLessonCollectable[];
}

interface ParticipantLessonCollectable {
  categoryName: string;
  imagePath: string;
  title: string;
  slideId: number;
  createdAt: string;
}

interface GetParticipantLessonCollectablesParams {
  keyword?: string;
  pageNumber: number;
  pageSize: number;
}

export const getParticipantLessonCollectables = async ({
  ...params
}: GetParticipantLessonCollectablesParams) => {
  const { data } = await http
    .get<ApiResponse<ParticipantLessonCollectableApi>>(
      `${baseUrl}/api/assessments/participant-lesson-collectable/participant`,
      { params }
    )
    .catch(() => ({ data: { data: { totalRecords: 0, data: [] } } }));
  return data.data;
};

export const getLessonCollectableById = async (id: number) => {
  const { data } = await http.get(
    `${baseUrl}/api/assessments/participant-lesson-collectable/${id}`
  );
  return data.data;
};

export const getLessonCollectableBySlideId = async (slideId: number) => {
  const { data } = await http.get(
    `${baseUrl}/api/assessments/participant-lesson-collectable/slide/${slideId}`
  );
  return data.data;
};

export const getSlideById = async (id: number) => {
  const { data } = await http.get(`${baseUrl}/api/assessments/slide/${id}`);
  return data.data;
};
